import { Component, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MatDialogRef } from '@angular/material/dialog';
import { IPeriod } from '@interfaces/IPeriod';
import { IAppState } from '@myStore/app.state';
import {
  ClearCreatePeriodErrors,
  ClearUpdatePeriodErrors,
  CreatePeriodRequest,
  UpdatePeriodRequest,
} from '@myStore/periods-changes/periods-changes.actions';
import {
  selectPeriodChangesFailure,
  selectPeriodChangesLoading,
  selectPeriodChangesSuccess,
} from '@myStore/periods-changes/periods-changes.selector';
import {
  ClearGetPeriod,
  ClearGroupPeriods,
  GetGroupPeriodsRequest,
  GetPeriodRequest,
} from '@myStore/periods-view/periods-view.actions';
import {
  selectPeriod,
  selectPeriodLoading,
} from '@myStore/periods-view/periods-view.selector';
import { select, Store } from '@ngrx/store';
import { CustomDateAdapter } from '@shared/adapter/date.adapter';
import { FormDialogComponent } from '@shared/components/UI/form-dialog/form-dialog.component';
import { formatDatePicker } from '@shared/utils/dateUtils';
import { isControlsIsValid } from '@shared/utils/formUtils';
import { MyErrorStateMatcher } from '@validators/matcher';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { GlobalConstants } from '@globalConstants';

@Component({
  selector: 'app-period-form',
  templateUrl: './period-form.component.html',
  styleUrls: ['./period-form.component.sass'],
  providers: [
    {
      provide: DateAdapter,
      useClass: CustomDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: GlobalConstants.DEFAULT_DATEPICKER_FORMAT,
    },
  ],
})
export class PeriodFormComponent implements OnInit, OnDestroy {
  private destroy$: Subject<boolean> = new Subject<boolean>();
  public sendLoading$ = this._store.pipe(select(selectPeriodChangesLoading));
  public periodLoading$ = false;
  public period: IPeriod | null = null;
  public errors: { [key: string]: string[] } | null = null;
  public days = {
    one: {
      '1': 'Понедельник',
      '2': 'Вторник',
      '3': 'Среда',
      '4': 'Четверг',
      '5': 'Пятница',
      '6': 'Суббота',
    },
    two: {
      '1-4': 'Понедельник-четверг',
      '1-5': 'Понедельник-пятница',
      '2-4': 'Вторник-четверг',
      '2-5': 'Вторник-пятница',
      '3-5': 'Среда-пятница',
      '3-6': 'Среда-суббота',
    },
  };
  public matcher = new MyErrorStateMatcher();
  public isEdit?: boolean;
  public periodId?: number;
  public groupId!: number;
  public dialogData = this._dialogRef.componentInstance.data;
  public requiredErrorMessage = 'Поле является обазательным';
  public periodForm = new UntypedFormGroup({
    start_date: new UntypedFormControl(GlobalConstants.dateFormat),
    lesson_days: new UntypedFormControl(),
    lesson_time: new UntypedFormControl('19:00'),
    webinar_days: new UntypedFormControl('3'),
    webinar_time: new UntypedFormControl('19:00'),
  });
  public ESDPPeriodForm = new UntypedFormGroup({
    lesson_days: new UntypedFormControl(),
  });

  constructor(
    private _store: Store<IAppState>,
    private _dialogRef: MatDialogRef<FormDialogComponent>
  ) {}

  ngOnInit(): void {
    this.isEdit = this.dialogData.isEdit;

    if (this.dialogData.editIdItem) {
      this.periodId = this.dialogData.editIdItem;
    }

    if (this.dialogData.groupId) {
      this.groupId = this.dialogData.groupId;
    }

    this._store
      .pipe(select(selectPeriodChangesFailure), takeUntil(this.destroy$))
      .subscribe(errors => {
        this.errors = errors;

        if (this.period?.is_esdp) {
          this.setESDPControlErrors(errors);
        } else {
          this.setControlErrors(errors);
        }
      });

    if (this.isEdit && this.periodId) {
      this.actionsForEdit(this.periodId);
    }

    this._store
      .pipe(select(selectPeriodChangesSuccess), takeUntil(this.destroy$))
      .subscribe(success => {
        if (success) {
          this.onCancel();
          this._store.dispatch(new ClearGroupPeriods());
          this._store.dispatch(new GetGroupPeriodsRequest(this.groupId));
        }
      });
  }

  get start_date() {
    return this.periodForm.get('start_date');
  }

  get lesson_days() {
    return this.periodForm.get('lesson_days');
  }

  get lesson_time() {
    return this.periodForm.get('lesson_time');
  }

  get webinar_days() {
    return this.periodForm.get('webinar_days');
  }

  get webinar_time() {
    return this.periodForm.get('webinar_time');
  }

  get ESDPLessonDays() {
    return this.ESDPPeriodForm.get('lesson_days');
  }

  onSubmit() {
    if (this.period?.id && this.period?.is_esdp && this.ESDPLessonDays?.value) {
      this._store.dispatch(
        new UpdatePeriodRequest(this.period.id, {
          lesson_days: this.ESDPLessonDays.value,
        })
      );

      return;
    }

    const formData = this.periodForm.value;
    let startDate = formData.start_date;

    if (startDate && startDate._i && typeof startDate._i !== 'string') {
      startDate = formatDatePicker(startDate._i);
    } else if (startDate && startDate._i) {
      startDate = startDate._i;
    }

    if (isControlsIsValid(this.periodForm.controls)) {
      const periodData = {
        ...formData,
        start_date: startDate,
        group_id: this.groupId,
      };

      if (this.isEdit && this.period && this.period.id) {
        this._store.dispatch(
          new UpdatePeriodRequest(this.period.id, periodData)
        );
      } else {
        this._store.dispatch(new CreatePeriodRequest(periodData));
      }
    }
  }

  actionsForEdit(id: number) {
    this._store.dispatch(new GetPeriodRequest(id));

    this._store
      .pipe(select(selectPeriod), takeUntil(this.destroy$))
      .subscribe(period => {
        this.period = period;

        if (period?.lesson_days && period?.is_esdp) {
          this.ESDPLessonDays?.patchValue(period.lesson_days.numbers);
          return;
        }

        if (period && period.lesson_days && period.webinar_days) {
          const data = {
            lesson_days: period.lesson_days.numbers,
            webinar_days: period.webinar_days.numbers,
          };
          this.periodForm.patchValue(data || {});
        }
      });

    this._store
      .pipe(select(selectPeriodLoading), takeUntil(this.destroy$))
      .subscribe(loading => {
        this.periodLoading$ = loading;
      });
  }

  onCancel() {
    this._dialogRef.close();
  }

  getMessageFormError(name: string): string | null {
    if (this.errors && this.errors[name]) {
      return this.errors[name][0];
    }

    return null;
  }

  setControlErrors(errors: { [key: string]: string[] } | null): void {
    if (errors) {
      Object.keys(errors).forEach(key => {
        if (this.periodForm.controls[key]) {
          this.periodForm.controls[key].setErrors({ errors: errors[key] });
        }
      });
    }
  }

  setESDPControlErrors(errors: { [key: string]: string[] } | null): void {
    if (errors) {
      Object.keys(errors).forEach(key => {
        if (this.ESDPPeriodForm.controls[key]) {
          this.ESDPPeriodForm.controls[key].setErrors({ errors: errors[key] });
        }
      });
    }
  }

  ngOnDestroy(): void {
    if (this.isEdit) {
      this._store.dispatch(new ClearUpdatePeriodErrors());
      this._store.dispatch(new ClearGetPeriod());
    } else {
      this._store.dispatch(new ClearCreatePeriodErrors());
    }

    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
